import { useQuery } from "@tanstack/react-query"

import {
  IAppConfig,
  IIFrameBridgeConfig,
  IIdleModeConfig,
  IKeyboardConfig,
  ISponsorContentConfig,
  IScreenSaverConfig,
  IStepConfig,
  fetchAppConfig,
} from "services/venue"
import config from "config"

import {
  prepareExtensionConfig,
  prepareFeatureQuickSearchConfig,
  prepareMapConfig,
  prepareStepAutoplayConfig,
  prepareSearchConfig,
  prepareParkingFinderConfig,
  preparePageConfig,
  prepareIdleModeConfig,
  getIndexPathConfig,
  prepareVenueFeatureQuickSearchConfig,
  getDefaultRoutePathConfig,
  prepareVenueControllerConfig,
  prepareKeyboardConfig,
  prepareScreenSaverConfig,
  prepareSponsoredContentConfig,
  prepareIframeBridgeConfig,
} from "providers/venue/utils/prepareConfig"

interface IPreparedAppConfig extends IAppConfig {
  idleModeConfig: IIdleModeConfig
  iframeBridgeConfig: IIFrameBridgeConfig
  keyboardConfig?: IKeyboardConfig
  sponsoredContentConfig?: ISponsorContentConfig
  screenSaverConfig?: IScreenSaverConfig
  stepAutoplayConfig?: IStepConfig
}

const DEFAULT_REMOTE_CONFIG = {
  map: {},
} as IAppConfig

const DEFAULT_APP_CONFIG = {
  map: {},
  idleModeConfig: {},
  iframeBridgeConfig: {},
  keyboardConfig: {},
  sponsoredContentConfig: {},
  stepAutoplayConfig: { delay: 3000, enable: false },
} as IPreparedAppConfig

export const useGetAppConfig = () => {
  return useQuery<IPreparedAppConfig | undefined, unknown, IPreparedAppConfig>({
    queryKey: ["app-config"],
    queryFn: async () => {
      try {
        const deviceType = config("device", "mobile")

        const remoteConfig = await fetchAppConfig()
        const appConfig = remoteConfig || DEFAULT_REMOTE_CONFIG

        const mapConfig = prepareMapConfig(appConfig.map)
        const stepAutoplayConfig = prepareStepAutoplayConfig(
          appConfig,
          deviceType
        )
        const parkingFinderConfig = prepareParkingFinderConfig(
          appConfig,
          deviceType
        )
        const pageConfig = preparePageConfig(appConfig, deviceType)
        const idleModeConfig = prepareIdleModeConfig(appConfig, deviceType)
        const iframeBridgeConfig = prepareIframeBridgeConfig(
          appConfig,
          deviceType
        )
        const searchConfig = prepareSearchConfig(appConfig, deviceType)
        const featureQuickSearchConfig = prepareFeatureQuickSearchConfig(
          appConfig,
          deviceType
        )
        const venueFeatureQuickSearchConfig =
          prepareVenueFeatureQuickSearchConfig(appConfig, deviceType)
        const keyboardConfig = prepareKeyboardConfig(appConfig, deviceType)
        const extensionConfig = prepareExtensionConfig(appConfig, deviceType)
        const sponsoredContentConfig = prepareSponsoredContentConfig(
          appConfig,
          deviceType
        )
        const screenSaverConfig = prepareScreenSaverConfig(
          appConfig,
          deviceType
        )
        const venueControllerConfig = prepareVenueControllerConfig(
          appConfig,
          deviceType
        )

        const indexPath = getIndexPathConfig(appConfig, deviceType)
        const defaultRoutePath = getDefaultRoutePathConfig(
          appConfig,
          deviceType
        )

        return {
          ...appConfig,
          mapConfig,
          stepAutoplayConfig,
          parkingFinderConfig,
          pageConfig,
          idleModeConfig,
          iframeBridgeConfig,
          searchConfig,
          featureQuickSearchConfig,
          venueFeatureQuickSearchConfig,
          keyboardConfig,
          extensionConfig,
          screenSaverConfig,
          sponsoredContentConfig,
          venueControllerConfig,
          indexPath,
          defaultRoutePath,
        }
      } catch (err) {
        return DEFAULT_APP_CONFIG
      }
    },
    initialData: DEFAULT_APP_CONFIG,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
